import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  Users, Calendar, BarChart3, Terminal, Send, Trash2, RefreshCw, 
  ChevronRight, AlertCircle, CheckCircle, Clock, TrendingUp, Activity, 
  X, Eye, Shield, UserCog, FileText, Globe, Cookie, Settings,
  ArrowUp, ArrowDown, Minus, Mail, Phone, Building2, Loader2,
  Plus, Edit2, Save, MessageSquare, Bell, Target, DollarSign,
  CalendarPlus, ExternalLink, Check, XCircle, UserCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOpsConsole = ({ isOpen, onClose, authToken, clientData }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Live Traffic data
  const [trafficData, setTrafficData] = useState({ visitors: [], stats: {} });
  const [trafficLoading, setTrafficLoading] = useState(false);
  
  // Consent data
  const [consentData, setConsentData] = useState({ consents: [], stats: {} });
  const [consentLoading, setConsentLoading] = useState(false);
  
  // Admins data
  const [adminsData, setAdminsData] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  
  // Leads data
  const [leadsData, setLeadsData] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  
  // Appointments data
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  
  // Inquiries data
  const [inquiriesData, setInquiriesData] = useState([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  
  // Data Requests (GDPR) data
  const [dataRequestsData, setDataRequestsData] = useState([]);
  const [dataRequestsLoading, setDataRequestsLoading] = useState(false);
  
  // Legal Page Views data
  const [legalViewsData, setLegalViewsData] = useState({ views: [], stats: {} });
  const [legalViewsLoading, setLegalViewsLoading] = useState(false);
  
  // Modal states
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false);
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Terminal
  const [command, setCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const logEndRef = useRef(null);

  // Google Calendar state
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (isOpen && authToken) {
      addLog('SYS', 'Admin Ops Console online');
      fetchDashboard();
      fetchTraffic();
      fetchLeads();
      fetchAppointments();
    }
  }, [isOpen, authToken]);

  const addLog = (type, message) => {
    const time = new Date().toLocaleTimeString('en-GB');
    setCommandHistory(prev => [...prev, { time, type, message }].slice(-50));
    setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // Fetch main dashboard data
  const fetchDashboard = async () => {
    if (!authToken) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ops/dashboard`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        addLog('SYS', 'Dashboard data synced');
      }
    } catch (error) {
      addLog('ERR', `Dashboard sync failed: ${error.message}`);
    }
    setIsLoading(false);
  };

  // Fetch live traffic
  const fetchTraffic = async () => {
    if (!authToken) return;
    setTrafficLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/visitors/live`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrafficData(data);
      }
    } catch (error) {
      console.error('Traffic fetch error:', error);
    }
    setTrafficLoading(false);
  };

  // Fetch consent data
  const fetchConsent = async () => {
    if (!authToken) return;
    setConsentLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/consent/history`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConsentData(data);
      }
    } catch (error) {
      console.error('Consent fetch error:', error);
    }
    setConsentLoading(false);
  };

  // Fetch admins
  const fetchAdmins = async () => {
    if (!authToken) return;
    setAdminsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clients/admins`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminsData(data.admins || []);
      }
    } catch (error) {
      console.error('Admins fetch error:', error);
    }
    setAdminsLoading(false);
  };

  // Fetch leads
  const fetchLeads = async () => {
    if (!authToken) return;
    setLeadsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeadsData(data.leads || []);
      }
    } catch (error) {
      console.error('Leads fetch error:', error);
    }
    setLeadsLoading(false);
  };

  // Fetch appointments - uses /api/clients/admin/all-bookings which returns client records with booking data
  const fetchAppointments = async () => {
    if (!authToken) return;
    setAppointmentsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clients/admin/all-bookings`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Map the response to appointment format
        const appointments = (data.bookings || []).map(booking => ({
          booking_id: booking.client_id, // Use client_id as booking_id for updates
          name: booking.name,
          full_name: booking.name,
          email: booking.email,
          phone: booking.phone,
          company_name: booking.company_name,
          preferred_date: booking.preferred_date,
          preferred_time: booking.preferred_time,
          date: booking.preferred_date,
          time: booking.preferred_time,
          service_interested: booking.business_category || 'Consultation',
          service: booking.business_category || 'Consultation',
          status: booking.status || 'confirmed',
          notes: booking.notes,
          booking_reference: booking.booking_reference,
          created_at: booking.created_at
        }));
        setAppointmentsData(appointments);
      }
    } catch (error) {
      console.error('Appointments fetch error:', error);
    }
    setAppointmentsLoading(false);
  };

  // Fetch customer inquiries (admin view)
  const fetchInquiries = async () => {
    if (!authToken) return;
    setInquiriesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer-portal/inquiries/admin`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInquiriesData(data.inquiries || []);
        addLog('SYS', `Loaded ${data.count || 0} customer inquiries`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        addLog('ERR', `Failed to fetch inquiries: ${errorData.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Inquiries fetch error:', error);
      addLog('ERR', `Inquiries fetch error: ${error.message}`);
    }
    setInquiriesLoading(false);
  };

  // Fetch GDPR Data Requests
  const fetchDataRequests = async () => {
    if (!authToken) return;
    setDataRequestsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/data-requests`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDataRequestsData(data.requests || []);
        addLog('SYS', `Loaded ${data.count || 0} GDPR data requests`);
      } else {
        addLog('ERR', 'Failed to fetch data requests');
      }
    } catch (error) {
      console.error('Data requests fetch error:', error);
      addLog('ERR', `Data requests fetch error: ${error.message}`);
    }
    setDataRequestsLoading(false);
  };

  // Update data request status
  const updateDataRequestStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/data-requests/${requestId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        addLog('SYS', `Data request ${requestId.slice(0, 8)}... marked as ${newStatus}`);
        fetchDataRequests();
      } else {
        addLog('ERR', 'Failed to update data request status');
      }
    } catch (error) {
      addLog('ERR', `Update error: ${error.message}`);
    }
  };

  // Fetch Legal Page Views
  const fetchLegalViews = async () => {
    if (!authToken) return;
    setLegalViewsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/legal-page-views`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLegalViewsData(data);
        addLog('SYS', `Loaded ${data.total || 0} legal page views`);
      } else {
        addLog('ERR', 'Failed to fetch legal page views');
      }
    } catch (error) {
      console.error('Legal views fetch error:', error);
      addLog('ERR', `Legal views fetch error: ${error.message}`);
    }
    setLegalViewsLoading(false);
  };

  // Handle terminal command
  const handleCommand = async (explicitCommand = null) => {
    const cmdToRun = explicitCommand || command;
    if (!cmdToRun.trim() || !authToken) return;
    addLog('CMD', `> ${cmdToRun}`);
    
    try {
      const response = await fetch(`${API_URL}/api/ops/command`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ command: cmdToRun })
      });
      
      if (response.ok) {
        const data = await response.json();
        addLog('RES', data.response);
        if (data.data) {
          if (Array.isArray(data.data)) {
            data.data.forEach(item => addLog('DAT', `  • ${item}`));
          } else {
            Object.entries(data.data).forEach(([k, v]) => addLog('DAT', `  ${k}: ${v}`));
          }
        }
      } else {
        addLog('ERR', 'Command failed');
      }
    } catch (error) {
      addLog('ERR', error.message);
    }
    setCommand('');
  };

  const clearLog = () => {
    setCommandHistory([]);
    addLog('SYS', 'Terminal cleared');
  };

  const getConsentActionLabel = (action) => {
    switch (action) {
      case 'accepted_all': return { label: 'Accepted All', color: 'bg-green-100 text-green-800' };
      case 'rejected_non_essential': return { label: 'Essential Only', color: 'bg-yellow-100 text-yellow-800' };
      case 'custom_selection': return { label: 'Custom', color: 'bg-blue-100 text-blue-800' };
      default: return { label: action, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getLeadStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return 'bg-blue-500';
      case 'contacted': return 'bg-yellow-500';
      case 'qualified': return 'bg-green-500';
      case 'unresponsive': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (!isOpen) return null;

  const isSuperAdmin = clientData?.admin_role === 'super_admin';

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'inquiries', label: 'Inquiries', icon: MessageSquare },
    { id: 'dataRequests', label: 'GDPR Requests', icon: UserCheck },
    { id: 'legalViews', label: 'Legal Pages', icon: FileText },
    { id: 'traffic', label: 'Live Traffic', icon: Globe },
    { id: 'consent', label: 'Consent', icon: Cookie },
    ...(isSuperAdmin ? [{ id: 'admins', label: 'Admins', icon: UserCog }] : []),
    { id: 'terminal', label: 'Terminal', icon: Terminal }
  ];

  return createPortal(
    <div 
      className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="relative w-full max-w-6xl h-[85vh] bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden flex flex-col"
        data-testid="admin-ops-console"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-mono text-sm">ONLINE</span>
              </div>
              <div className="h-6 w-px bg-slate-600"></div>
              <div>
                <h1 className="text-xl font-bold text-white">MANAGEMENT HUB</h1>
                <p className="text-slate-400 text-sm font-mono">
                  {currentTime.toLocaleDateString('en-GB')} • {currentTime.toLocaleTimeString('en-GB')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-white font-medium">{clientData?.client_id}</p>
                <p className="text-slate-400 text-xs capitalize">{clientData?.admin_role?.replace('_', ' ')}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                data-testid="ops-console-close"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex-shrink-0 bg-slate-800/50 border-b border-slate-700 px-4 overflow-x-auto">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'traffic') fetchTraffic();
                  if (tab.id === 'consent') fetchConsent();
                  if (tab.id === 'admins') fetchAdmins();
                  if (tab.id === 'leads') fetchLeads();
                  if (tab.id === 'schedule') fetchAppointments();
                  if (tab.id === 'dataRequests') fetchDataRequests();
                  if (tab.id === 'legalViews') fetchLegalViews();
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-cyan-400 border-cyan-400 bg-cyan-500/10'
                    : 'text-slate-400 border-transparent hover:text-white hover:bg-slate-700/50'
                }`}
                data-testid={`ops-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-6">
              
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <DashboardTab 
                  dashboardData={dashboardData}
                  trafficData={trafficData}
                  isLoading={isLoading}
                  onRefresh={fetchDashboard}
                  onOpenLeads={() => setActiveTab('leads')}
                  onOpenSchedule={() => setActiveTab('schedule')}
                  onOpenPipeline={() => setShowPipelineModal(true)}
                  onOpenSystem={() => setShowSystemModal(true)}
                />
              )}

              {/* Leads Tab */}
              {activeTab === 'leads' && (
                <LeadsTab 
                  leads={leadsData}
                  loading={leadsLoading}
                  onRefresh={fetchLeads}
                  onAddLead={() => setShowAddLeadModal(true)}
                  onEditLead={(lead) => { setSelectedLead(lead); setShowEditLeadModal(true); }}
                  authToken={authToken}
                  addLog={addLog}
                />
              )}

              {/* Schedule Tab */}
              {activeTab === 'schedule' && (
                <ScheduleTab 
                  appointments={appointmentsData}
                  loading={appointmentsLoading}
                  onRefresh={fetchAppointments}
                  onEditAppointment={(apt) => { setSelectedAppointment(apt); setShowEditAppointmentModal(true); }}
                  calendarConnected={calendarConnected}
                  setCalendarConnected={setCalendarConnected}
                  authToken={authToken}
                  addLog={addLog}
                />
              )}

              {/* Inquiries Tab */}
              {activeTab === 'inquiries' && (
                <InquiriesTab 
                  inquiries={inquiriesData}
                  loading={inquiriesLoading}
                  onRefresh={fetchInquiries}
                  authToken={authToken}
                  addLog={addLog}
                />
              )}

              {/* GDPR Data Requests Tab */}
              {activeTab === 'dataRequests' && (
                <DataRequestsTab 
                  requests={dataRequestsData}
                  loading={dataRequestsLoading}
                  onRefresh={fetchDataRequests}
                  onUpdateStatus={updateDataRequestStatus}
                />
              )}

              {/* Legal Page Views Tab */}
              {activeTab === 'legalViews' && (
                <LegalViewsTab 
                  data={legalViewsData}
                  loading={legalViewsLoading}
                  onRefresh={fetchLegalViews}
                />
              )}

              {/* Live Traffic Tab */}
              {activeTab === 'traffic' && (
                <TrafficTab 
                  trafficData={trafficData}
                  loading={trafficLoading}
                  onRefresh={fetchTraffic}
                />
              )}

              {/* Consent Tab */}
              {activeTab === 'consent' && (
                <ConsentTab 
                  consentData={consentData}
                  loading={consentLoading}
                  onRefresh={fetchConsent}
                  getConsentActionLabel={getConsentActionLabel}
                />
              )}

              {/* Admins Tab */}
              {activeTab === 'admins' && isSuperAdmin && (
                <AdminsTab 
                  admins={adminsData}
                  loading={adminsLoading}
                  onRefresh={fetchAdmins}
                />
              )}

              {/* Terminal Tab */}
              {activeTab === 'terminal' && (
                <TerminalTab 
                  commandHistory={commandHistory}
                  command={command}
                  setCommand={setCommand}
                  handleCommand={handleCommand}
                  clearLog={clearLog}
                  logEndRef={logEndRef}
                />
              )}

            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <AddLeadModal 
          onClose={() => setShowAddLeadModal(false)}
          onSuccess={() => { setShowAddLeadModal(false); fetchLeads(); fetchDashboard(); }}
          authToken={authToken}
          addLog={addLog}
        />
      )}

      {/* Edit Lead Modal */}
      {showEditLeadModal && selectedLead && (
        <EditLeadModal 
          lead={selectedLead}
          onClose={() => { setShowEditLeadModal(false); setSelectedLead(null); }}
          onSuccess={() => { setShowEditLeadModal(false); setSelectedLead(null); fetchLeads(); fetchDashboard(); }}
          authToken={authToken}
          addLog={addLog}
        />
      )}

      {/* Edit Appointment Modal */}
      {showEditAppointmentModal && selectedAppointment && (
        <EditAppointmentModal 
          appointment={selectedAppointment}
          onClose={() => { setShowEditAppointmentModal(false); setSelectedAppointment(null); }}
          onSuccess={() => { setShowEditAppointmentModal(false); setSelectedAppointment(null); fetchAppointments(); fetchDashboard(); }}
          authToken={authToken}
          addLog={addLog}
        />
      )}

      {/* Pipeline Modal */}
      {showPipelineModal && (
        <PipelineModal 
          dashboardData={dashboardData}
          onClose={() => setShowPipelineModal(false)}
          authToken={authToken}
          onRefresh={fetchDashboard}
        />
      )}

      {/* System Modal */}
      {showSystemModal && (
        <SystemModal 
          dashboardData={dashboardData}
          onClose={() => setShowSystemModal(false)}
          authToken={authToken}
          onRefresh={fetchDashboard}
        />
      )}
    </div>,
    document.body
  );
};

// ==================== DASHBOARD TAB ====================
const DashboardTab = ({ dashboardData, trafficData, isLoading, onRefresh, onOpenLeads, onOpenSchedule, onOpenPipeline, onOpenSystem }) => (
  <div className="space-y-6">
    {/* Quick Stats Grid */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard label="Today's Leads" value={dashboardData?.leads?.new_leads_24h || 0} icon={Users} color="cyan" />
      <StatCard label="Appointments" value={dashboardData?.schedule?.todays_appointments || 0} icon={Calendar} color="emerald" />
      <StatCard label="Active Visitors" value={trafficData?.stats?.active_now || 0} icon={Eye} color="purple" />
      <StatCard label="Conversion" value={`${dashboardData?.pipeline?.conversion_rate || 0}%`} icon={TrendingUp} color="amber" />
    </div>

    {/* Interactive Module Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Leads Module - Clickable */}
      <button onClick={onOpenLeads} className="text-left">
        <ModuleCard
          title="LEADS"
          subtitle="click to manage"
          icon={Users}
          color="from-cyan-500 to-blue-600"
          clickable
          items={[
            { label: 'New (24h)', value: dashboardData?.leads?.new_leads_24h || 0 },
            { label: 'Unresponded', value: dashboardData?.leads?.unresponded || 0, alert: true },
            { label: 'Follow-up needed', value: dashboardData?.leads?.follow_up_needed || 0 }
          ]}
        />
      </button>

      {/* Schedule Module - Clickable */}
      <button onClick={onOpenSchedule} className="text-left">
        <ModuleCard
          title="SCHEDULE"
          subtitle="click to manage"
          icon={Calendar}
          color="from-emerald-500 to-teal-600"
          clickable
          items={[
            { label: "Today's appointments", value: dashboardData?.schedule?.todays_appointments || 0 },
            { label: 'No-show risk', value: dashboardData?.schedule?.no_show_risk || 0, alert: true },
            { label: 'Reminder needed', value: dashboardData?.schedule?.reminder_needed || 0 }
          ]}
        />
      </button>

      {/* Pipeline Module - Clickable */}
      <button onClick={onOpenPipeline} className="text-left">
        <ModuleCard
          title="PIPELINE"
          subtitle="click to edit"
          icon={TrendingUp}
          color="from-purple-500 to-pink-600"
          clickable
          items={[
            { label: 'Active deals', value: dashboardData?.pipeline?.active_deals || 0 },
            { label: 'Conversion rate', value: `${dashboardData?.pipeline?.conversion_rate || 0}%` },
            { label: 'This month', value: dashboardData?.pipeline?.this_month_wins || 0 }
          ]}
        />
      </button>

      {/* System Module - Clickable */}
      <button onClick={onOpenSystem} className="text-left">
        <ModuleCard
          title="SYSTEM"
          subtitle="click to configure"
          icon={Activity}
          color="from-amber-500 to-orange-600"
          clickable
          items={[
            { label: 'Response rate', value: `${dashboardData?.health?.response_rate || 0}%` },
            { label: 'Show rate', value: `${dashboardData?.health?.show_rate || 100}%` },
            { label: 'Completion rate', value: `${dashboardData?.health?.completion_rate || 0}%` }
          ]}
        />
      </button>
    </div>

    {/* Refresh Button */}
    <div className="flex justify-center">
      <Button onClick={onRefresh} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={isLoading}>
        <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
        Refresh Dashboard
      </Button>
    </div>
  </div>
);

// ==================== LEADS TAB ====================
const LeadsTab = ({ leads, loading, onRefresh, onAddLead, onEditLead, authToken, addLog }) => {
  const [filter, setFilter] = useState('all');
  
  const filteredLeads = leads.filter(lead => {
    if (filter === 'all') return true;
    if (filter === 'unresponded') return !lead.contacted && lead.status === 'new';
    if (filter === 'followup') return lead.needs_followup;
    return lead.status === filter;
  });

  const handleQuickAction = async (leadId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });
      if (response.ok) {
        addLog('SYS', `Lead ${action} successful`);
        onRefresh();
      }
    } catch (error) {
      addLog('ERR', `Lead action failed: ${error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-900/50 text-blue-300 border-blue-700/50';
      case 'contacted': return 'bg-yellow-900/50 text-yellow-300 border-yellow-700/50';
      case 'qualified': return 'bg-green-900/50 text-green-300 border-green-700/50';
      case 'unresponsive': return 'bg-red-900/50 text-red-300 border-red-700/50';
      default: return 'bg-slate-700 text-slate-300 border-slate-600';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
          <Users className="w-5 h-5 text-cyan-400" />
          Lead Management
          <span className="text-sm font-normal text-slate-400">({filteredLeads.length})</span>
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm flex-1 sm:flex-none min-w-[140px]"
            data-testid="leads-filter"
          >
            <option value="all">All Leads</option>
            <option value="unresponded">Unresponded</option>
            <option value="followup">Needs Follow-up</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
          </select>
          <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={onAddLead} className="bg-cyan-600 hover:bg-cyan-700 text-sm" data-testid="add-lead-btn">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Lead</span>
          </Button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block sm:hidden space-y-3">
        {filteredLeads.length > 0 ? (
          filteredLeads.map((lead, index) => (
            <div key={index} className="bg-slate-800 rounded-xl border border-slate-700 p-4" data-testid={`lead-card-${index}`}>
              {/* Header with name and status */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-white font-semibold">{lead.first_name} {lead.last_name}</p>
                  <p className="text-slate-400 text-sm">{lead.company || 'No company'}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(lead.status)}`}>
                  {lead.status || 'New'}
                </span>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-2 mb-3">
                <a href={`mailto:${lead.email}`} className="text-cyan-400 text-sm flex items-center gap-2 hover:text-cyan-300">
                  <Mail className="w-4 h-4" /> {lead.email}
                </a>
                {lead.phone && (
                  <p className="text-slate-400 text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4" /> {lead.phone}
                  </p>
                )}
                <p className="text-slate-500 text-xs flex items-center gap-2">
                  <Globe className="w-3 h-3" /> Source: {lead.source || 'Website'}
                </p>
              </div>
              
              {/* Quick Call Button - One Tap Calling */}
              {lead.phone && (
                <a 
                  href={`tel:${lead.phone}`}
                  className="w-full mb-3 py-3 px-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-green-900/30 active:scale-[0.98] transition-all"
                  data-testid={`quick-call-${index}`}
                >
                  <Phone className="w-5 h-5" />
                  Quick Call {lead.first_name}
                </a>
              )}
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-3 border-t border-slate-700">
                <button 
                  onClick={() => handleQuickAction(lead.lead_id || lead._id, 'mark_contacted')}
                  className="flex-1 py-2 px-3 bg-green-600/20 hover:bg-green-600/40 rounded-lg text-green-400 text-sm flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" /> Contacted
                </button>
                <button 
                  onClick={() => handleQuickAction(lead.lead_id || lead._id, 'set_followup')}
                  className="flex-1 py-2 px-3 bg-amber-600/20 hover:bg-amber-600/40 rounded-lg text-amber-400 text-sm flex items-center justify-center gap-2"
                >
                  <Bell className="w-4 h-4" /> Follow-up
                </button>
                <button 
                  onClick={() => onEditLead(lead)}
                  className="py-2 px-3 bg-cyan-600/20 hover:bg-cyan-600/40 rounded-lg text-cyan-400"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center text-slate-400">
            {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'No leads found'}
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredLeads.length > 0 ? (
                filteredLeads.map((lead, index) => (
                  <tr key={index} className="hover:bg-slate-700/50" data-testid={`lead-row-${index}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">{lead.first_name} {lead.last_name}</p>
                        <p className="text-slate-400 text-sm">{lead.company || 'No company'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <p className="text-slate-300 text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </p>
                        {lead.phone && (
                          <p className="text-slate-400 text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {lead.phone}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status || 'New'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{lead.source || 'Website'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleQuickAction(lead.lead_id || lead._id, 'mark_contacted')}
                          className="p-1.5 bg-green-600/20 hover:bg-green-600/40 rounded text-green-400"
                          title="Mark as Contacted"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleQuickAction(lead.lead_id || lead._id, 'set_followup')}
                          className="p-1.5 bg-amber-600/20 hover:bg-amber-600/40 rounded text-amber-400"
                          title="Set Follow-up"
                        >
                          <Bell className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => onEditLead(lead)}
                          className="p-1.5 bg-cyan-600/20 hover:bg-cyan-600/40 rounded text-cyan-400"
                          title="Edit Lead"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'No leads found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== SCHEDULE TAB ====================
const ScheduleTab = ({ appointments, loading, onRefresh, onEditAppointment, calendarConnected, setCalendarConnected, authToken, addLog }) => {
  const [filter, setFilter] = useState('all');
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [checkingCalendar, setCheckingCalendar] = useState(false);
  
  const filteredAppointments = appointments.filter(apt => {
    if (filter === 'all') return true;
    if (filter === 'today') {
      const today = new Date().toDateString();
      return new Date(apt.preferred_date || apt.date).toDateString() === today;
    }
    return apt.status === filter;
  });

  // Check calendar status on mount
  useEffect(() => {
    const checkCalendarStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/oauth/calendar/status`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCalendarStatus(data);
          setCalendarConnected(data.connected);
        }
      } catch (error) {
        console.error('Calendar status check failed:', error);
      }
    };
    if (authToken) checkCalendarStatus();
  }, [authToken, setCalendarConnected]);

  const handleConnectCalendar = async () => {
    if (calendarConnected) {
      // Disconnect
      try {
        const response = await fetch(`${API_URL}/api/oauth/calendar/disconnect`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          setCalendarConnected(false);
          setCalendarStatus(prev => ({ ...prev, connected: false, email: null }));
          addLog('SYS', 'Google Calendar disconnected');
        }
      } catch (error) {
        addLog('ERR', `Failed to disconnect: ${error.message}`);
      }
      return;
    }

    setCheckingCalendar(true);
    try {
      const response = await fetch(`${API_URL}/api/oauth/calendar/login`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.authorization_url) {
          addLog('SYS', 'Redirecting to Google OAuth...');
          window.open(data.authorization_url, '_blank', 'width=500,height=600');
        }
      } else {
        const error = await response.json();
        if (error.detail?.includes('not configured')) {
          addLog('INFO', 'Google Calendar requires setup by admin.');
          addLog('SYS', 'Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to backend/.env to enable calendar sync.');
        } else {
          addLog('ERR', error.detail || 'Failed to start calendar connection');
        }
      }
    } catch (error) {
      addLog('ERR', `Calendar connection failed: ${error.message}`);
    }
    setCheckingCalendar(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-400" />
          Scheduled Appointments
        </h2>
        <div className="flex items-center gap-2">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Appointments</option>
            <option value="today">Today</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
          <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button 
            onClick={handleConnectCalendar} 
            variant="outline" 
            className={`${calendarConnected ? 'border-green-600 text-green-400 hover:bg-green-600/20' : 'border-emerald-600 text-emerald-400 hover:bg-emerald-600/20'}`}
            disabled={checkingCalendar}
            data-testid="connect-calendar-btn"
          >
            {checkingCalendar ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CalendarPlus className="w-4 h-4 mr-2" />
            )}
            {calendarConnected ? 'Disconnect Calendar' : 'Connect Calendar'}
          </Button>
        </div>
      </div>

      {/* Calendar Sync Info */}
      <div className={`${calendarConnected ? 'bg-green-900/20 border-green-700/50' : 'bg-emerald-900/20 border-emerald-700/50'} border rounded-xl p-4`}>
        <div className="flex items-start gap-3">
          <CalendarPlus className={`w-5 h-5 ${calendarConnected ? 'text-green-400' : 'text-emerald-400'} mt-0.5`} />
          <div>
            <h3 className={`${calendarConnected ? 'text-green-300' : 'text-emerald-300'} font-medium`}>
              {calendarConnected ? 'Google Calendar Connected' : 'Google Calendar Sync'}
            </h3>
            <p className={`${calendarConnected ? 'text-green-400/70' : 'text-emerald-400/70'} text-sm mt-1`}>
              {calendarConnected && calendarStatus?.email 
                ? `Connected as ${calendarStatus.email}. Appointments can be synced to your calendar.` 
                : calendarStatus?.message || 'Connect Google Calendar to sync appointments in real-time. Appointments will automatically appear in your calendar.'}
            </p>
          </div>
        </div>
      </div>

      {/* Appointments Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Date & Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredAppointments.length > 0 ? (
                filteredAppointments.map((apt, index) => (
                  <tr key={index} className="hover:bg-slate-700/50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">{apt.name || apt.full_name || 'Unknown'}</p>
                        <p className="text-slate-400 text-sm">{apt.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white">{new Date(apt.preferred_date || apt.date).toLocaleDateString('en-GB')}</p>
                        <p className="text-slate-400 text-sm">{apt.preferred_time || apt.time || 'TBD'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{apt.service_interested || apt.service || 'Consultation'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        apt.status === 'confirmed' ? 'bg-green-900/50 text-green-300' :
                        apt.status === 'pending' ? 'bg-yellow-900/50 text-yellow-300' :
                        apt.status === 'completed' ? 'bg-blue-900/50 text-blue-300' :
                        apt.status === 'cancelled' ? 'bg-red-900/50 text-red-300' :
                        'bg-slate-700 text-slate-300'
                      }`}>
                        {apt.status || 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button 
                        onClick={() => onEditAppointment(apt)}
                        className="p-1.5 bg-cyan-600/20 hover:bg-cyan-600/40 rounded text-cyan-400"
                        title="Edit Appointment"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'No appointments found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== INQUIRIES TAB ====================
const InquiriesTab = ({ inquiries, loading, onRefresh, authToken, addLog }) => {
  const [filter, setFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);

  // Filter inquiries
  const filteredInquiries = inquiries.filter(inq => {
    if (filter === 'all') return true;
    return inq.status === filter;
  });

  // Respond to inquiry
  const handleRespond = async (inquiryId) => {
    if (!responseText.trim()) return;
    setResponding(true);
    try {
      const response = await fetch(`${API_URL}/api/customer-portal/inquiries/${inquiryId}/respond?response=${encodeURIComponent(responseText)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        addLog('SYS', `Response sent to inquiry ${inquiryId.slice(0, 8)}`);
        setSelectedInquiry(null);
        setResponseText('');
        onRefresh();
      }
    } catch (error) {
      addLog('ERR', `Failed to respond: ${error.message}`);
    }
    setResponding(false);
  };

  // Close inquiry
  const handleClose = async (inquiryId) => {
    try {
      const response = await fetch(`${API_URL}/api/customer-portal/inquiries/${inquiryId}/close`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        addLog('SYS', `Inquiry ${inquiryId.slice(0, 8)} closed`);
        onRefresh();
      }
    } catch (error) {
      addLog('ERR', `Failed to close: ${error.message}`);
    }
  };

  // Forward to WhatsApp
  const forwardToWhatsApp = (inquiry) => {
    const phone = '447471343190'; // Peter's WhatsApp
    const message = `📩 *New Customer Inquiry*\n\n` +
      `*From:* ${inquiry.customer_name || 'Customer'}\n` +
      `*Email:* ${inquiry.customer_email}\n` +
      `*Subject:* ${inquiry.subject}\n\n` +
      `*Message:*\n${inquiry.message}\n\n` +
      `_Received: ${new Date(inquiry.created_at).toLocaleString('en-GB')}_`;
    
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    addLog('SYS', `Forwarded inquiry to WhatsApp`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-900/50 text-yellow-300';
      case 'responded': return 'bg-green-900/50 text-green-300';
      case 'closed': return 'bg-slate-700 text-slate-300';
      default: return 'bg-slate-700 text-slate-300';
    }
  };

  // Auto-fetch on mount
  useEffect(() => {
    onRefresh();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-purple-400" />
          Customer Inquiries
        </h2>
        <div className="flex items-center gap-2">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
            data-testid="inquiries-filter"
          >
            <option value="all">All Inquiries</option>
            <option value="pending">Pending</option>
            <option value="responded">Responded</option>
            <option value="closed">Closed</option>
          </select>
          <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-xl p-4">
          <p className="text-yellow-400 text-sm">Pending</p>
          <p className="text-2xl font-bold text-yellow-300">{inquiries.filter(i => i.status === 'pending').length}</p>
        </div>
        <div className="bg-green-900/20 border border-green-700/50 rounded-xl p-4">
          <p className="text-green-400 text-sm">Responded</p>
          <p className="text-2xl font-bold text-green-300">{inquiries.filter(i => i.status === 'responded').length}</p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <p className="text-slate-400 text-sm">Total</p>
          <p className="text-2xl font-bold text-white">{inquiries.length}</p>
        </div>
      </div>

      {/* Inquiries List */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="divide-y divide-slate-700 max-h-[400px] overflow-y-auto">
          {filteredInquiries.length > 0 ? (
            filteredInquiries.map((inquiry) => (
              <div key={inquiry.inquiry_id} className="p-4 hover:bg-slate-700/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                      <span className="text-slate-400 text-xs">
                        {new Date(inquiry.created_at).toLocaleString('en-GB')}
                      </span>
                    </div>
                    <h4 className="text-white font-medium truncate">{inquiry.subject}</h4>
                    <p className="text-slate-400 text-sm">
                      From: {inquiry.customer_name || 'Customer'} ({inquiry.customer_email})
                    </p>
                    <p className="text-slate-300 text-sm mt-2 line-clamp-2">{inquiry.message}</p>
                    {inquiry.response && (
                      <div className="mt-2 p-2 bg-green-900/20 rounded-lg border border-green-700/30">
                        <p className="text-green-300 text-xs font-medium">Response:</p>
                        <p className="text-green-200 text-sm">{inquiry.response}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => forwardToWhatsApp(inquiry)}
                      className="p-2 bg-green-600/20 hover:bg-green-600/40 rounded-lg text-green-400"
                      title="Forward to WhatsApp"
                    >
                      <Phone className="w-4 h-4" />
                    </button>
                    {inquiry.status === 'pending' && (
                      <button
                        onClick={() => setSelectedInquiry(inquiry)}
                        className="p-2 bg-cyan-600/20 hover:bg-cyan-600/40 rounded-lg text-cyan-400"
                        title="Respond"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {inquiry.status !== 'closed' && (
                      <button
                        onClick={() => handleClose(inquiry.inquiry_id)}
                        className="p-2 bg-slate-600/20 hover:bg-slate-600/40 rounded-lg text-slate-400"
                        title="Close Inquiry"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-4 py-12 text-center text-slate-400">
              {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'No inquiries found'}
            </div>
          )}
        </div>
      </div>

      {/* Response Modal */}
      {selectedInquiry && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && setSelectedInquiry(null)}>
          <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-lg p-6" data-testid="respond-inquiry-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-cyan-400" />
                Respond to Inquiry
              </h3>
              <button onClick={() => setSelectedInquiry(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-slate-400 text-sm">Subject: <span className="text-white">{selectedInquiry.subject}</span></p>
                <p className="text-slate-400 text-sm">From: <span className="text-white">{selectedInquiry.customer_name} ({selectedInquiry.customer_email})</span></p>
                <p className="text-slate-300 text-sm mt-2">{selectedInquiry.message}</p>
              </div>
              <div>
                <label className="text-slate-400 text-sm">Your Response</label>
                <textarea 
                  value={responseText} 
                  onChange={(e) => setResponseText(e.target.value)} 
                  className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1" 
                  rows={4}
                  placeholder="Type your response..."
                  data-testid="inquiry-response-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => forwardToWhatsApp(selectedInquiry)} variant="outline" className="flex-1 border-green-600 text-green-400 hover:bg-green-600/20">
                  <Phone className="w-4 h-4 mr-2" />
                  Forward to WhatsApp
                </Button>
                <Button onClick={() => handleRespond(selectedInquiry.inquiry_id)} className="flex-1 bg-cyan-600 hover:bg-cyan-700" disabled={responding || !responseText.trim()} data-testid="send-response-btn">
                  {responding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Response
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DATA REQUESTS TAB (GDPR) ====================
const DataRequestsTab = ({ requests, loading, onRefresh, onUpdateStatus }) => {
  const [filter, setFilter] = useState('all');

  const filteredRequests = requests.filter(req => {
    if (filter === 'all') return true;
    return req.status === filter;
  });

  // Calculate days remaining until 30-day deadline
  const getDaysRemaining = (createdAt) => {
    const created = new Date(createdAt);
    const deadline = new Date(created.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
    const now = new Date();
    const diff = deadline - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  const getDeadlineStatus = (createdAt, status) => {
    if (status === 'completed' || status === 'rejected') return null;
    const days = getDaysRemaining(createdAt);
    if (days <= 0) return { text: 'OVERDUE', color: 'text-red-400 bg-red-900/50 border-red-600/50', urgent: true };
    if (days <= 3) return { text: `${days}d left`, color: 'text-red-400 bg-red-900/50 border-red-600/50', urgent: true };
    if (days <= 7) return { text: `${days}d left`, color: 'text-orange-400 bg-orange-900/50 border-orange-600/50', urgent: true };
    if (days <= 14) return { text: `${days}d left`, color: 'text-yellow-400 bg-yellow-900/50 border-yellow-600/50', urgent: false };
    return { text: `${days}d left`, color: 'text-green-400 bg-green-900/50 border-green-600/50', urgent: false };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-900/50 text-yellow-300 border-yellow-600/50';
      case 'in_progress': return 'bg-blue-900/50 text-blue-300 border-blue-600/50';
      case 'completed': return 'bg-green-900/50 text-green-300 border-green-600/50';
      case 'rejected': return 'bg-red-900/50 text-red-300 border-red-600/50';
      default: return 'bg-slate-700 text-slate-300 border-slate-600';
    }
  };

  const getRequestTypeLabel = (type) => {
    const labels = {
      'access': 'Access Data',
      'rectification': 'Correct Data',
      'erasure': 'Delete Data',
      'restriction': 'Restrict Processing',
      'portability': 'Data Portability',
      'objection': 'Object to Processing',
      'withdraw': 'Withdraw Consent'
    };
    return labels[type] || type;
  };

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case 'access': return '📋';
      case 'rectification': return '✏️';
      case 'erasure': return '🗑️';
      case 'restriction': return '⏸️';
      case 'portability': return '📤';
      case 'objection': return '🚫';
      case 'withdraw': return '↩️';
      default: return '📝';
    }
  };

  useEffect(() => {
    onRefresh();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-purple-400" />
          GDPR Data Requests
        </h2>
        <div className="flex items-center gap-2">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Requests</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <p className="text-slate-400 text-sm">Total Requests</p>
          <p className="text-2xl font-bold text-white">{requests.length}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-yellow-600/30">
          <p className="text-yellow-400 text-sm">Pending</p>
          <p className="text-2xl font-bold text-yellow-300">{requests.filter(r => r.status === 'pending').length}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-blue-600/30">
          <p className="text-blue-400 text-sm">In Progress</p>
          <p className="text-2xl font-bold text-blue-300">{requests.filter(r => r.status === 'in_progress').length}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-green-600/30">
          <p className="text-green-400 text-sm">Completed</p>
          <p className="text-2xl font-bold text-green-300">{requests.filter(r => r.status === 'completed').length}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-red-600/30">
          <p className="text-red-400 text-sm flex items-center gap-1">
            <Clock className="w-3 h-3" /> Urgent (≤7d)
          </p>
          <p className="text-2xl font-bold text-red-300">
            {requests.filter(r => {
              if (r.status === 'completed' || r.status === 'rejected') return false;
              const days = getDaysRemaining(r.submitted_at || r.created_at);
              return days <= 7;
            }).length}
          </p>
        </div>
      </div>

      {/* UK GDPR Compliance Note */}
      <div className="bg-purple-900/20 border border-purple-600/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-purple-300 font-medium">UK GDPR Compliance</p>
            <p className="text-slate-400 text-sm">Data subject requests must be responded to within 30 days. Complex requests may take up to 90 days with notification.</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-12">
          <UserCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No data requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div 
              key={request.request_id} 
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 hover:border-purple-600/50 transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                {/* Request Info */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{getRequestTypeIcon(request.requestType)}</span>
                    <div>
                      <h3 className="text-white font-semibold">{getRequestTypeLabel(request.requestType)}</h3>
                      <p className="text-slate-400 text-sm">ID: {request.request_id?.slice(0, 8)}...</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Users className="w-4 h-4 text-slate-500" />
                      <span>{request.fullName}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <span>{request.email}</span>
                    </div>
                  </div>

                  {request.additionalInfo && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-slate-400 text-xs mb-1">Additional Info:</p>
                      <p className="text-slate-300 text-sm">{request.additionalInfo}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Submitted: {new Date(request.submitted_at || request.created_at).toLocaleString('en-GB')}
                    </span>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex flex-col items-end gap-3">
                  <div className="flex items-center gap-2">
                    {/* Deadline Countdown */}
                    {(() => {
                      const deadline = getDeadlineStatus(request.submitted_at || request.created_at, request.status);
                      if (deadline) {
                        return (
                          <span className={`px-2 py-1 rounded-full text-xs font-bold border ${deadline.color} ${deadline.urgent ? 'animate-pulse' : ''}`}>
                            ⏱️ {deadline.text}
                          </span>
                        );
                      }
                      return null;
                    })()}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                      {request.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    {request.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => onUpdateStatus(request.request_id, 'in_progress')}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                        >
                          Start Processing
                        </Button>
                      </>
                    )}
                    {request.status === 'in_progress' && (
                      <>
                        <Button
                          onClick={() => onUpdateStatus(request.request_id, 'completed')}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Complete
                        </Button>
                        <Button
                          onClick={() => onUpdateStatus(request.request_id, 'rejected')}
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-600/20 text-xs"
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {(request.status === 'completed' || request.status === 'rejected') && (
                      <Button
                        onClick={() => onUpdateStatus(request.request_id, 'pending')}
                        size="sm"
                        variant="outline"
                        className="border-slate-600 text-slate-400 hover:bg-slate-700 text-xs"
                      >
                        Reopen
                      </Button>
                    )}
                    <a
                      href={`mailto:${request.email}?subject=RE: Your Data Request (${request.request_id?.slice(0, 8)})`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-md transition-colors"
                    >
                      <Mail className="w-3 h-3" />
                      Email
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== LEGAL PAGE VIEWS TAB ====================
const LegalViewsTab = ({ data, loading, onRefresh }) => {
  const { views = [], stats = {} } = data;

  const getPageIcon = (page) => {
    switch (page) {
      case 'privacy-policy': return '🔒';
      case 'terms-of-service': return '📋';
      case 'cookie-policy': return '🍪';
      case 'data-request': return '📝';
      default: return '📄';
    }
  };

  const getPageLabel = (page) => {
    const labels = {
      'privacy-policy': 'Privacy Policy',
      'terms-of-service': 'Terms of Service',
      'cookie-policy': 'Cookie Policy',
      'data-request': 'Data Request Form'
    };
    return labels[page] || page;
  };

  useEffect(() => {
    onRefresh();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-400" />
          Legal Page Views
        </h2>
        <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-xl p-4 border border-blue-600/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🔒</span>
            <p className="text-blue-400 text-sm">Privacy Policy</p>
          </div>
          <p className="text-2xl font-bold text-blue-300">{stats.privacy_policy || 0}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-purple-600/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📋</span>
            <p className="text-purple-400 text-sm">Terms of Service</p>
          </div>
          <p className="text-2xl font-bold text-purple-300">{stats.terms_of_service || 0}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-yellow-600/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🍪</span>
            <p className="text-yellow-400 text-sm">Cookie Policy</p>
          </div>
          <p className="text-2xl font-bold text-yellow-300">{stats.cookie_policy || 0}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 border border-green-600/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📝</span>
            <p className="text-green-400 text-sm">Data Request Form</p>
          </div>
          <p className="text-2xl font-bold text-green-300">{stats.data_request || 0}</p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-900/20 border border-blue-600/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-300 font-medium">Legal Page Analytics</p>
            <p className="text-slate-400 text-sm">Track which clients are viewing your legal pages. Useful for compliance monitoring and understanding user engagement with privacy documentation.</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : views.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No legal page views recorded yet</p>
          <p className="text-slate-500 text-sm mt-2">Views will appear here when users visit your legal pages</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-800/80">
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Page</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Visitor</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Email</th>
                  <th className="text-left p-4 text-slate-400 font-medium text-sm">Time</th>
                </tr>
              </thead>
              <tbody>
                {views.map((view, index) => (
                  <tr key={view.view_id || index} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getPageIcon(view.page)}</span>
                        <span className="text-white font-medium text-sm">{getPageLabel(view.page)}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm font-mono">{view.visitor_id || 'Anonymous'}</span>
                    </td>
                    <td className="p-4">
                      {view.user_email ? (
                        <span className="text-cyan-400 text-sm">{view.user_email}</span>
                      ) : (
                        <span className="text-slate-500 text-sm italic">Not logged in</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm">
                        {new Date(view.timestamp).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TRAFFIC TAB ====================
const TrafficTab = ({ trafficData, loading, onRefresh }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Globe className="w-5 h-5 text-cyan-400" />
        Live Traffic Monitor
      </h2>
      <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <p className="text-slate-400 text-sm">Active Now</p>
        <p className="text-3xl font-bold text-green-400">{trafficData?.stats?.active_now || 0}</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <p className="text-slate-400 text-sm">Today</p>
        <p className="text-3xl font-bold text-cyan-400">{trafficData?.stats?.today || 0}</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <p className="text-slate-400 text-sm">This Week</p>
        <p className="text-3xl font-bold text-purple-400">{trafficData?.stats?.this_week || 0}</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <p className="text-slate-400 text-sm">All Time</p>
        <p className="text-3xl font-bold text-white">{trafficData?.stats?.total || 0}</p>
      </div>
    </div>

    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700">
        <h3 className="font-semibold text-white">Active Visitors</h3>
      </div>
      <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
        {trafficData?.visitors?.length > 0 ? (
          trafficData.visitors.map((visitor, index) => (
            <div key={index} className="px-4 py-3 flex items-center justify-between hover:bg-slate-700/50">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <div>
                  <p className="text-white font-mono text-sm">{visitor.visitor_id?.slice(0, 12) || 'Anonymous'}...</p>
                  <p className="text-slate-400 text-xs">{visitor.page || '/'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-slate-300 text-sm">{visitor.device || 'Desktop'}</p>
                <p className="text-slate-500 text-xs">{visitor.country || 'Unknown'}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="px-4 py-8 text-center text-slate-400">No active visitors right now</div>
        )}
      </div>
    </div>
  </div>
);

// ==================== CONSENT TAB ====================
const ConsentTab = ({ consentData, loading, onRefresh, getConsentActionLabel }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Cookie className="w-5 h-5 text-amber-400" />
        Cookie Consent History
      </h2>
      <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <p className="text-slate-400 text-sm">Total</p>
        <p className="text-3xl font-bold text-white">{consentData?.stats?.total || 0}</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 border border-green-900/50">
        <p className="text-slate-400 text-sm">Accepted All</p>
        <p className="text-3xl font-bold text-green-400">{consentData?.stats?.accepted_all || 0}</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 border border-yellow-900/50">
        <p className="text-slate-400 text-sm">Essential Only</p>
        <p className="text-3xl font-bold text-yellow-400">{consentData?.stats?.rejected_non_essential || 0}</p>
      </div>
      <div className="bg-slate-800 rounded-xl p-4 border border-blue-900/50">
        <p className="text-slate-400 text-sm">Custom</p>
        <p className="text-3xl font-bold text-blue-400">{consentData?.stats?.custom_selection || 0}</p>
      </div>
    </div>

    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700">
        <h3 className="font-semibold text-white">Recent Consent Records</h3>
      </div>
      <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
        {consentData?.consents?.length > 0 ? (
          consentData.consents.slice(0, 50).map((consent, index) => {
            const actionInfo = getConsentActionLabel(consent.action);
            return (
              <div key={index} className="px-4 py-3 flex items-center justify-between hover:bg-slate-700/50">
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-white font-mono text-sm">{consent.visitor_id?.slice(0, 16) || 'Unknown'}...</p>
                    <p className="text-slate-400 text-xs">{new Date(consent.timestamp).toLocaleString('en-GB')}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionInfo.color}`}>{actionInfo.label}</span>
              </div>
            );
          })
        ) : (
          <div className="px-4 py-8 text-center text-slate-400">
            {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'No consent records found'}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ==================== ADMINS TAB ====================
const AdminsTab = ({ admins, loading, onRefresh }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <UserCog className="w-5 h-5 text-purple-400" />
        Admin Management
      </h2>
      <Button onClick={onRefresh} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={loading}>
        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </Button>
    </div>

    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
      <div className="divide-y divide-slate-700">
        {admins?.length > 0 ? (
          admins.map((admin, index) => (
            <div key={index} className="px-4 py-4 flex items-center justify-between hover:bg-slate-700/50">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${admin.admin_role === 'super_admin' ? 'bg-purple-600' : 'bg-slate-600'}`}>
                  <UserCog className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">{admin.client_id}</p>
                  <p className="text-slate-400 text-sm capitalize">{admin.admin_role?.replace('_', ' ')}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${admin.admin_role === 'super_admin' ? 'bg-purple-900/50 text-purple-300 border border-purple-700' : 'bg-slate-700 text-slate-300'}`}>
                {admin.admin_role === 'super_admin' ? 'Super Admin' : 'Limited Admin'}
              </span>
            </div>
          ))
        ) : (
          <div className="px-4 py-8 text-center text-slate-400">
            {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'No admin accounts found'}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ==================== TERMINAL TAB ====================
const TerminalTab = ({ commandHistory, command, setCommand, handleCommand, clearLog, logEndRef }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <Terminal className="w-5 h-5 text-green-400" />
        Command Terminal
      </h2>
      <Button onClick={clearLog} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700">
        <Trash2 className="w-4 h-4 mr-2" />
        Clear
      </Button>
    </div>

    <div className="bg-black/50 rounded-xl border border-slate-700 p-4 h-96 overflow-y-auto font-mono text-sm">
      {commandHistory.map((log, index) => (
        <div key={index} className="flex gap-2 py-0.5">
          <span className="text-slate-500">[{log.time}]</span>
          <span className={`font-bold ${log.type === 'CMD' ? 'text-cyan-400' : log.type === 'ERR' ? 'text-red-400' : log.type === 'RES' ? 'text-green-400' : log.type === 'DAT' ? 'text-yellow-400' : 'text-slate-300'}`}>{log.type}</span>
          <span className="text-slate-300">{log.message}</span>
        </div>
      ))}
      <div ref={logEndRef} />
    </div>

    <div className="flex gap-2">
      <Input value={command} onChange={(e) => setCommand(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleCommand()} placeholder="Enter command (help, status, leads, appointments...)" className="flex-1 bg-slate-800 border-slate-600 text-white font-mono" />
      <Button onClick={() => handleCommand()} className="bg-cyan-600 hover:bg-cyan-700">
        <Send className="w-4 h-4" />
      </Button>
    </div>

    <div className="flex flex-wrap gap-2">
      {['help', 'status', 'leads', 'appointments', 'traffic'].map(cmd => (
        <button key={cmd} onClick={() => handleCommand(cmd)} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 text-sm font-mono">{cmd}</button>
      ))}
    </div>
  </div>
);

// ==================== STAT CARD ====================
const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400'
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-4 border`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
        </div>
        <Icon className={`w-8 h-8 ${colors[color].split(' ').pop()}`} />
      </div>
    </div>
  );
};

// ==================== MODULE CARD ====================
const ModuleCard = ({ title, subtitle, icon: Icon, color, items, clickable }) => (
  <div className={`bg-slate-800 rounded-xl border border-slate-700 overflow-hidden ${clickable ? 'hover:border-slate-500 transition-colors cursor-pointer' : ''}`}>
    <div className={`bg-gradient-to-r ${color} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-white" />
          <div>
            <h3 className="font-bold text-white">{title}</h3>
            <p className="text-white/70 text-xs">{subtitle}</p>
          </div>
        </div>
        {clickable && <ChevronRight className="w-5 h-5 text-white/70" />}
      </div>
    </div>
    <div className="p-4 space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center justify-between py-1">
          <span className="text-slate-400 text-sm">{item.label}</span>
          <span className={`font-mono font-bold ${item.alert && item.value > 0 ? 'text-amber-400' : 'text-white'}`}>{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

// ==================== ADD LEAD MODAL ====================
const AddLeadModal = ({ onClose, onSuccess, authToken, addLog }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', company: '', source: 'manual', status: 'new', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (response.ok) {
        addLog('SYS', `Lead added: ${form.first_name} ${form.last_name}`);
        onSuccess();
      } else {
        addLog('ERR', 'Failed to add lead');
      }
    } catch (error) {
      addLog('ERR', error.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="add-lead-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-lead-modal">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Plus className="w-5 h-5 text-cyan-400" />Add New Lead</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="add-lead-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4" data-testid="add-lead-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm">First Name *</label>
              <Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="add-lead-first-name" required />
            </div>
            <div>
              <label className="text-slate-400 text-sm">Last Name</label>
              <Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="add-lead-last-name" />
            </div>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Email *</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="add-lead-email" required />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Phone</label>
            <Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="add-lead-phone" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Company</label>
            <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="add-lead-company" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Status</label>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1" data-testid="add-lead-status">
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="unresponsive">Unresponsive</option>
            </select>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1" data-testid="add-lead-notes" rows={3} />
          </div>
          <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 py-3" disabled={saving} data-testid="add-lead-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Lead
          </Button>
        </form>
      </div>
    </div>
  );
};

// ==================== EDIT LEAD MODAL ====================
const EditLeadModal = ({ lead, onClose, onSuccess, authToken, addLog }) => {
  const [form, setForm] = useState({
    first_name: lead.first_name || '',
    last_name: lead.last_name || '',
    email: lead.email || '',
    phone: lead.phone || '',
    company: lead.company || '',
    status: lead.status || 'new',
    notes: lead.notes || '',
    needs_followup: lead.needs_followup || false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/${lead.lead_id || lead._id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (response.ok) {
        addLog('SYS', `Lead updated: ${form.first_name} ${form.last_name}`);
        onSuccess();
      } else {
        addLog('ERR', 'Failed to update lead');
      }
    } catch (error) {
      addLog('ERR', error.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="edit-lead-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-lead-modal">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Edit2 className="w-5 h-5 text-cyan-400" />Edit Lead</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="edit-lead-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4" data-testid="edit-lead-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm">First Name</label>
              <Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-lead-first-name" />
            </div>
            <div>
              <label className="text-slate-400 text-sm">Last Name</label>
              <Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-lead-last-name" />
            </div>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Email</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-lead-email" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Phone</label>
            <Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-lead-phone" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Company</label>
            <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-lead-company" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Status</label>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1" data-testid="edit-lead-status">
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="unresponsive">Unresponsive</option>
            </select>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
            <input type="checkbox" id="followup" checked={form.needs_followup} onChange={(e) => setForm({...form, needs_followup: e.target.checked})} className="w-5 h-5 rounded" data-testid="edit-lead-followup" />
            <label htmlFor="followup" className="text-slate-300 text-sm">Needs Follow-up</label>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1" data-testid="edit-lead-notes" rows={3} />
          </div>
          <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 py-3" disabled={saving} data-testid="edit-lead-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </form>
      </div>
    </div>
  );
};

// ==================== EDIT APPOINTMENT MODAL ====================
const EditAppointmentModal = ({ appointment, onClose, onSuccess, authToken, addLog }) => {
  const [form, setForm] = useState({
    name: appointment.name || appointment.full_name || '',
    email: appointment.email || '',
    phone: appointment.phone || '',
    preferred_date: appointment.preferred_date || appointment.date || '',
    preferred_time: appointment.preferred_time || appointment.time || '',
    service_interested: appointment.service_interested || appointment.service || '',
    status: appointment.status || 'pending',
    notes: appointment.notes || appointment.message || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${appointment.booking_id || appointment._id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (response.ok) {
        addLog('SYS', `Appointment updated: ${form.name}`);
        onSuccess();
      } else {
        addLog('ERR', 'Failed to update appointment');
      }
    } catch (error) {
      addLog('ERR', error.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="edit-appointment-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md p-6" data-testid="edit-appointment-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-400" />Edit Appointment</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="edit-appointment-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-appointment-form">
          <div>
            <label className="text-slate-400 text-sm">Client Name</label>
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-appointment-name" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Email</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-appointment-email" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Phone</label>
            <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-appointment-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm">Date</label>
              <Input type="date" value={form.preferred_date?.split('T')[0] || ''} onChange={(e) => setForm({...form, preferred_date: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-appointment-date" />
            </div>
            <div>
              <label className="text-slate-400 text-sm">Time</label>
              <Input type="time" value={form.preferred_time} onChange={(e) => setForm({...form, preferred_time: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-appointment-time" />
            </div>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Service</label>
            <Input value={form.service_interested} onChange={(e) => setForm({...form, service_interested: e.target.value})} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="edit-appointment-service" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Status</label>
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1" data-testid="edit-appointment-status">
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1" data-testid="edit-appointment-notes" rows={3} />
          </div>
          <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} data-testid="edit-appointment-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </form>
      </div>
    </div>
  );
};

// ==================== PIPELINE MODAL ====================
const PipelineModal = ({ dashboardData, onClose, authToken, onRefresh }) => {
  const [activeDeals, setActiveDeals] = useState(dashboardData?.pipeline?.active_deals || 0);
  const [conversionRate, setConversionRate] = useState(dashboardData?.pipeline?.conversion_rate || 0);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/ops/pipeline`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ active_deals: activeDeals, conversion_rate: conversionRate })
      });
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Pipeline save error:', error);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="pipeline-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md p-6" data-testid="pipeline-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-purple-400" />Pipeline Settings</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="pipeline-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm">Active Deals</label>
            <Input type="number" value={activeDeals} onChange={(e) => setActiveDeals(parseInt(e.target.value) || 0)} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="pipeline-active-deals" />
          </div>
          <div>
            <label className="text-slate-400 text-sm">Conversion Rate (%)</label>
            <Input type="number" value={conversionRate} onChange={(e) => setConversionRate(parseInt(e.target.value) || 0)} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="pipeline-conversion-rate" min={0} max={100} />
          </div>
          <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-3">
            <p className="text-purple-300 text-sm">Pipeline metrics help track your sales performance. Update these values as deals progress.</p>
          </div>
          <Button onClick={handleSave} className="w-full bg-purple-600 hover:bg-purple-700" disabled={saving} data-testid="pipeline-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== SYSTEM MODAL ====================
const SystemModal = ({ dashboardData, onClose, authToken, onRefresh }) => {
  const [responseRate, setResponseRate] = useState(dashboardData?.health?.response_rate || 0);
  const [showRate, setShowRate] = useState(dashboardData?.health?.show_rate || 100);
  const [completionRate, setCompletionRate] = useState(dashboardData?.health?.completion_rate || 0);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/ops/system`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ response_rate: responseRate, show_rate: showRate, completion_rate: completionRate })
      });
      onRefresh();
      onClose();
    } catch (error) {
      console.error('System save error:', error);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="system-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-md p-6" data-testid="system-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Activity className="w-5 h-5 text-amber-400" />System Configuration</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="system-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm">Response Rate (%)</label>
            <Input type="number" value={responseRate} onChange={(e) => setResponseRate(parseInt(e.target.value) || 0)} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="system-response-rate" min={0} max={100} />
            <p className="text-slate-500 text-xs mt-1">Percentage of leads contacted within 24 hours</p>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Show Rate (%)</label>
            <Input type="number" value={showRate} onChange={(e) => setShowRate(parseInt(e.target.value) || 0)} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="system-show-rate" min={0} max={100} />
            <p className="text-slate-500 text-xs mt-1">Percentage of appointments attended</p>
          </div>
          <div>
            <label className="text-slate-400 text-sm">Completion Rate (%)</label>
            <Input type="number" value={completionRate} onChange={(e) => setCompletionRate(parseInt(e.target.value) || 0)} className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="system-completion-rate" min={0} max={100} />
            <p className="text-slate-500 text-xs mt-1">Percentage of consultations converted to clients</p>
          </div>
          <Button onClick={handleSave} className="w-full bg-amber-600 hover:bg-amber-700" disabled={saving} data-testid="system-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminOpsConsole;
